import React, { useEffect, useMemo, useState } from "react";
import "./manage-contacts-page.scss";
import DataTable from "react-data-table-component";
import ContactHelper from "../../../../entities/contact/contact-helper";
import { useSelector } from "react-redux";
import AddContactModal from "../../components/add-contact-modal/add-contact-modal";
import EditContactModal from "../../components/edit-contact-modal/edit-contact-modal";
import DeleteContactModal from "../../components/delete-contact-modal/delete-contact-modal";
import { EventEmitter } from "../../../../shared/utils/event-emitter";
import Events from "../../../../const/events";
import Button from "../../../../shared/components/dashboard-layout/components/button/buttonComponent";
import Table from "../../../../shared/components/dashboard-layout/components/table/tableComponent";
import Modal from "../../../../shared/components/dashboard-layout/components/modal/modalComponent";
import Input from "../../../../shared/components/dashboard-layout/components/inputFields/inputComponent";

const ManageContactsPage = () => {
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchText, setSearchText] = useState("");
  const { contacts } = useSelector((state) => state.contactsStore);

  // Function to apply search filter and update contacts list
  const applySearchFilter = (contactsList, searchTerm = searchText) => {
    const filteredList = searchTerm
      ? contactsList.filter(
          (contact) =>
            (contact.name && contact.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (contact.phone && contact.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (contact.email && contact.email.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      : contactsList;

    return filteredList
      .map((contact) => ({
        id: contact._id,
        name: contact.name || "Unknown",
        phone: contact.phone || "N/A",
        email: contact.email || "N/A",
        dateAdded: new Date(contact.createdAt).toLocaleDateString(),
        createdAt: new Date(contact.createdAt),
        originalContact: contact, // Keep reference to original contact for platform checking
        platforms: contact.platforms || [],
      }))
      .sort((a, b) => b.createdAt - a.createdAt); // Sort by latest first
  };

  // Function to update and sort contacts
  const updateContacts = (contactsList) => {
    const processedContacts = applySearchFilter(contactsList);
    setFilteredContacts(processedContacts);
  };

  useEffect(() => {
    if (contacts) {
      updateContacts(contacts);
    }
  }, [contacts, searchText]); // Add searchText as dependency to re-filter when search changes

  const handleSearch = (e) => {
    const text = e.target.value;
    setSearchText(text);
    // The useEffect will handle the filtering when searchText changes
  };

  const handleClearSearch = () => {
    setSearchText("");
  };

  useEffect(() => {
    ContactHelper.fetchAllContacts(); // Initial fetch
  }, []);

  const handleContactAdded = () => {
    ContactHelper.fetchAllContacts(); // Refresh contacts after adding a new one
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setShowEditModal(true);
  };

  const handleContactUpdated = () => {
    ContactHelper.fetchAllContacts(); // Refresh contacts after updating - search filter will be preserved
  };

  const handleDeleteContact = (contact) => {
    setSelectedContact(contact);
    setShowDeleteModal(true);
  };

  const handleContactDeleted = () => {
    ContactHelper.fetchAllContacts(); // Refresh contacts after deleting
  };

  const hasValidPlatforms = (contact) => {
    if (!contact?.platforms) return false;
    
    // Check for WhatsApp (requires valid phone) - if they have WhatsApp, they can use both WhatsApp and WeChat QR
    const hasWhatsApp = contact.platforms.some(p => 
      p.on === "whatsapp" && contact.phone && contact.phone !== "N/A"
    );
    
    return hasWhatsApp;
  };

  const getAvailablePlatforms = (contact) => {
    if (!contact?.platforms) return [];
    
    const platforms = [];
    
    // Check for WhatsApp (requires valid phone)
    if (contact.platforms.some(p => p.on === "whatsapp") && contact.phone && contact.phone !== "N/A") {
      platforms.push("whatsapp");
      platforms.push("wechat"); // If they have WhatsApp, they can also use WeChat QR
    }
    
    return platforms;
  };

  const handleShowQr = (contact) => {
    if (!contact?.id || !hasValidPlatforms(contact)) return;
    
    const availablePlatforms = getAvailablePlatforms(contact);
    
    EventEmitter.dispatch(Events.SHOW_QR_CODE_POPUP, {
      id: contact.id,
      title: contact.name || "Contact",
      contact: contact.originalContact,
      platforms: availablePlatforms,
    });
  };

  const [modalShow, setModalShow] = useState(false);

  const tableColumns = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        cell: (row) => row.name,
      },
      {
        key: "phone",
        header: "Phone",
        cell: (row) => row.phone,
      },
      {
        key: "email",
        header: "Email",
        cell: (row) => row.email,
      },
      {
        key: "dateAdded",
        header: "Date Added",
        cell: (row) => row.dateAdded,
      },
      {
        key: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="d-flex gap-1">
            {hasValidPlatforms(row) && (
              <button
                className="bouncy-button small white"
                onClick={() => handleShowQr(row)}
                title="Show QR code"
                type="button"
              >
                <i className="mdi mdi-qrcode"></i>
              </button>
            )}
            <button
              className="bouncy-button small white"
              onClick={() => handleEditContact(row)}
              title="Edit contact name"
              type="button"
            >
              <i className="mdi mdi-pencil"></i>
            </button>
            <button
              className="bouncy-button small red"
              onClick={() => handleDeleteContact(row)}
              title="Delete contact"
              type="button"
            >
              <i className="mdi mdi-delete"></i>
            </button>
          </div>
        ),
      },
    ],
    [handleDeleteContact, handleEditContact, handleShowQr]
  );

  return (
    <div className="manage-contacts-page">
      <div className="pageHeaderTitle">
        <div className="pageTitle">
          <svg fill="var(--text-primary)" viewBox="0 0 16 16" id="contacts-16px" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path id="Path_131" data-name="Path 131" d="M33,6a2,2,0,1,0-2,2A2,2,0,0,0,33,6ZM31,7a1,1,0,1,1,1-1A1,1,0,0,1,31,7Zm7.5-1H38V3.5A3.5,3.5,0,0,0,34.5,0h-10a.5.5,0,0,0-.5.5V2.045h-.5a.5.5,0,0,0,0,1H24V6h-.5a.5.5,0,0,0,0,1H24v3h-.5a.5.5,0,0,0,0,1H24v3h-.5a.5.5,0,0,0,0,1H24v.5a.5.5,0,0,0,.5.5h10A3.5,3.5,0,0,0,38,12.5V10h.5a.5.5,0,0,0,.5-.5v-3A.5.5,0,0,0,38.5,6ZM38,9H37a1,1,0,0,1,0-2h1Zm-1,3.5A2.5,2.5,0,0,1,34.5,15h-9a.5.5,0,0,0,0-1H25V11h.5a.5.5,0,0,0,0-1H25V7h.5a.5.5,0,0,0,0-1H25V3.045h.545a.5.5,0,0,0,0-1H25V1h9.5A2.5,2.5,0,0,1,37,3.5V6a2,2,0,0,0,0,4ZM31.5,8h-1A3.5,3.5,0,0,0,27,11.5,1.5,1.5,0,0,0,28.5,13h5A1.5,1.5,0,0,0,35,11.5,3.5,3.5,0,0,0,31.5,8Zm2,4h-5a.5.5,0,0,1-.5-.5A2.5,2.5,0,0,1,30.5,9h1A2.5,2.5,0,0,1,34,11.5.5.5,0,0,1,33.5,12Z" transform="translate(-23)"></path> </g></svg>
          Contact List
        </div>

        <div className="right">
          {searchText && (
            <div className="search-info">
              <small className="text-muted">
                Showing {filteredContacts.length} of {contacts?.length || 0} contacts
              </small>
            </div>
          )}

          <div className="input-group">
            <span className="input-group-text">
              <i className="mdi mdi-magnify"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search contacts by name, phone, or email..."
              value={searchText}
              onChange={handleSearch}
            />
            {searchText && (
              <button
                type="button"
                className="btn"
                onClick={handleClearSearch}
                title="Clear search"
              >
                <i className="mdi mdi-close"></i>
              </button>
            )}
          </div>

          <Button
            className={"small"}
            onClick={() => setShowModal(true)}
          >
            <i className="fas fa-plus"></i> <span>Add Contact</span>
          </Button>
        </div>
      </div>

      {/* <Modal
        header={"Modal Title Here"}
        type={""} // default, success, warning, error
        isVisible={modalShow}
        onClose={() => setModalShow(false)}
        notes={"this is sample note"}
        cancelButtonLabel={"cancel"}
        cancelButtonAction={() => setModalShow(false)}
        // negativeButtonLabel={"negative"}
        positiveButtonLabel={"positive"}
        // neutralButtonLabel={"neutral"}
      >
        <Input />
        <Input label={"Label text goes here"}
          icon={<i class="fa fa-phone"></i>}
        />
        <Input type={"textarea"} />
        <Input
          label={"Label text goes here"}
          icon={<i class="fa fa-phone"></i>}
          type={"textarea"}
        />
        <Input label={"Label text for checkbox"} type={"checkbox"} />
        <Input label={"Label text for radio one"} type={"radio"} id={"one"} />
        <Input label={"Label text for radio two"} type={"radio"} id={"two"} />

        <Input
          label={"Search box demo input"}
          placeholder={"Search contacts by name"}
          icon={<i className="fa fa-search"></i>}
        />
      </Modal> */}


      <Table columns={tableColumns} data={filteredContacts} rowKey="id" />



      {/* <div className="tableWrapper manageContactsTable">
        <DataTable
          columns={[
            { name: "Name", selector: (row) => row.name, sortable: true },
            { name: "Phone", selector: (row) => row.phone, sortable: true },
            { name: "Email", selector: (row) => row.email, sortable: true },
            {
              name: "Date Added",
              selector: (row) => row.dateAdded,
              sortable: true,
            },
            {
              name: "Actions",
              cell: (row) => (
                <div className="d-flex gap-1">
                  {hasValidPlatforms(row) && (
                    <button
                      className="bouncy-button small white"
                      onClick={() => handleShowQr(row)}
                      title="Show QR code"
                    >
                      <i className="mdi mdi-qrcode"></i>
                    </button>
                  )}
                  <button
                    className="bouncy-button small white"
                    onClick={() => handleEditContact(row)}
                    title="Edit contact name"
                  >
                    <i className="mdi mdi-pencil"></i>
                  </button>
                  <button
                    className="bouncy-button small red"
                    onClick={() => handleDeleteContact(row)}
                    title="Delete contact"
                  >
                    <i className="mdi mdi-delete"></i>
                  </button>
                </div>
              ),
              ignoreRowClick: true,
              allowOverflow: true,
              button: true,
            },
          ]}
          data={filteredContacts}
          pagination
        />
      </div> */}

      <AddContactModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onContactAdded={handleContactAdded} // Refresh contacts when a new one is added
      />

      <EditContactModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        contact={selectedContact}
        onContactUpdated={handleContactUpdated}
      />

      <DeleteContactModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        contact={selectedContact}
        onContactDeleted={handleContactDeleted}
      />
    </div>
  );
};

export default ManageContactsPage;
